import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Drawer,
  Group,
  Loader,
  NumberInput,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { isAxiosError } from 'axios';
import { IconPlus } from '@tabler/icons-react';
import { apiClient } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { useAccounts } from './accountsApi';
import { ACCOUNT_TYPE } from './types';

// Phase Accounting-13: المصاريف — record an operating expense; the
// journal entry posts on save.

interface ExpenseDto {
  expenseId: string;
  expenseDate: string;
  accountCode: string;
  accountNameAr: string;
  accountNameEn: string | null;
  amount: number;
  paidFromCode: string;
  paidFromNameAr: string;
  description: string;
}

interface ExpensePageDto {
  total: number;
  rows: ExpenseDto[];
}

const QK = ['expenses'] as const;

export function ExpensesPage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: [...QK, page] as const,
    queryFn: async () =>
      (
        await apiClient.get<ExpensePageDto>('/api/accounting/expenses', {
          params: { page, pageSize: 50 },
        })
      ).data,
  });

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.accounting')}
        title={t('accounting.expenses.title')}
        subtitle={t('accounting.expenses.subtitle')}
      />

      <Paper p="md">
        <Group justify="flex-end">
          <Button leftSection={<IconPlus size={16} stroke={1.6} />} onClick={() => setCreateOpen(true)}>
            {t('accounting.expenses.create')}
          </Button>
        </Group>
      </Paper>

      {isLoading ? (
        <Stack align="center" py={48}><Loader size="sm" /></Stack>
      ) : (
        <Paper p={0}>
          <Table verticalSpacing="sm" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('accounting.expenses.columns.date')}</Table.Th>
                <Table.Th>{t('accounting.expenses.columns.account')}</Table.Th>
                <Table.Th>{t('accounting.expenses.columns.description')}</Table.Th>
                <Table.Th>{t('accounting.expenses.columns.paidFrom')}</Table.Th>
                <Table.Th style={{ textAlign: 'end' }}>{t('accounting.expenses.columns.amount')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(data?.rows ?? []).map((e) => (
                <Table.Tr key={e.expenseId}>
                  <Table.Td className="font-numeric">{e.expenseDate}</Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      <span className="font-numeric">{e.accountCode}</span>{' '}
                      {i18n.language === 'ar' ? e.accountNameAr : (e.accountNameEn ?? e.accountNameAr)}
                    </Text>
                  </Table.Td>
                  <Table.Td><Text size="sm" lineClamp={1}>{e.description}</Text></Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">{e.paidFromNameAr}</Text>
                  </Table.Td>
                  <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>
                    {e.amount.toFixed(2)}
                  </Table.Td>
                </Table.Tr>
              ))}
              {data && data.rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Text size="sm" c="dimmed" ta="center" py="lg">
                      {t('accounting.expenses.empty')}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
          {data && data.total > 50 && (
            <Group justify="center" p="md">
              <Pagination total={Math.ceil(data.total / 50)} value={page} onChange={setPage} />
            </Group>
          )}
        </Paper>
      )}

      <CreateExpenseDrawer
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => qc.invalidateQueries({ queryKey: QK })}
      />
    </Stack>
  );
}

function CreateExpenseDrawer({
  opened, onClose, onCreated,
}: { opened: boolean; onClose: () => void; onCreated: () => void }) {
  const { t, i18n } = useTranslation();
  const accounts = useAccounts(false);
  const [date, setDate] = useState<Date | null>(new Date());
  const [accountId, setAccountId] = useState<string | null>(null);
  const [paidFromId, setPaidFromId] = useState<string | null>(null);
  const [amount, setAmount] = useState<number>(0);
  const [description, setDescription] = useState('');

  const label = (a: { code: string; nameAr: string; nameEn: string | null }) =>
    `${a.code} — ${i18n.language === 'ar' ? a.nameAr : (a.nameEn ?? a.nameAr)}`;

  const expenseOptions = useMemo(
    () =>
      (accounts.data ?? [])
        .filter((a) => a.isPostable && a.isActive && a.accountType === ACCOUNT_TYPE.EXPENSE)
        .map((a) => ({ value: a.accountId, label: label(a) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accounts.data, i18n.language],
  );
  const payingOptions = useMemo(
    () =>
      (accounts.data ?? [])
        .filter((a) => a.isPostable && a.isActive && a.accountType === ACCOUNT_TYPE.ASSET)
        .map((a) => ({ value: a.accountId, label: label(a) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accounts.data, i18n.language],
  );

  const create = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post('/api/accounting/expenses', {
          expenseDate: toIsoDate(date!),
          accountId,
          amount,
          paidFromAccountId: paidFromId,
          branchId: null,
          supplierId: null,
          description: description.trim(),
        })
      ).data,
  });

  const valid =
    date !== null && accountId !== null && paidFromId !== null &&
    amount > 0 && description.trim() !== '';

  const submit = async () => {
    try {
      await create.mutateAsync();
      notifications.show({ color: 'green', title: t('common.toasts.created'), message: description });
      onCreated();
      onClose();
      setAccountId(null); setAmount(0); setDescription('');
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={t('accounting.expenses.create')}
      position={i18n.language === 'ar' ? 'left' : 'right'}
      size="md"
      padding="lg"
    >
      <Stack gap="md">
        <DatePickerInput
          label={t('accounting.expenses.form.dateLabel')}
          value={date}
          onChange={setDate}
        />
        <Select
          label={t('accounting.expenses.form.accountLabel')}
          description={t('accounting.expenses.form.accountHelp')}
          data={expenseOptions}
          value={accountId}
          onChange={setAccountId}
          searchable
          withAsterisk
        />
        <NumberInput
          label={t('accounting.expenses.form.amountLabel')}
          value={amount}
          onChange={(v) => setAmount(Number(v) || 0)}
          min={0}
          decimalScale={2}
          hideControls
        />
        <Select
          label={t('accounting.expenses.form.paidFromLabel')}
          data={payingOptions}
          value={paidFromId}
          onChange={setPaidFromId}
          searchable
          withAsterisk
        />
        <TextInput
          label={t('accounting.expenses.form.descriptionLabel')}
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          withAsterisk
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={create.isPending}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={!valid} loading={create.isPending}>
            {t('common.create')}
          </Button>
        </Group>
      </Stack>
    </Drawer>
  );
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function extractApiMessage(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; title?: string } | undefined;
    return data?.detail ?? data?.title ?? err.message;
  }
  return null;
}
